import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert, CircularProgress,
  Chip, Switch, Tooltip, useMediaQuery, useTheme
} from '@mui/material';
import { Add, Edit, Delete, Sync, CheckCircle, Error, PlayArrow } from '@mui/icons-material';
import axios from 'axios';

function FbSettings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCred, setEditingCred] = useState(null);
  const [syncOutput, setSyncOutput] = useState('');
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', app_id: '', app_secret: '', access_token: '', ad_account_id: '', business_portfolio: '' });

  useEffect(() => { fetchCredentials(); }, []);

  const fetchCredentials = async () => {
    try {
      const res = await axios.get('/admin/facebook/settings/credentials');
      setCredentials(res.data.data || []);
    } catch (error) { console.error('Error fetching credentials:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    try {
      if (editingCred) {
        await axios.put(`/admin/facebook/settings/credentials/${editingCred.id}`, formData);
        setMessage({ type: 'success', text: 'Credential updated successfully' });
      } else {
        await axios.post('/admin/facebook/settings/credentials', formData);
        setMessage({ type: 'success', text: 'Credential added successfully' });
      }
      setDialogOpen(false);
      resetForm();
      fetchCredentials();
    } catch (error) { setMessage({ type: 'error', text: 'Error saving credential' }); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this credential?')) return;
    try {
      await axios.delete(`/admin/facebook/settings/credentials/${id}`);
      setMessage({ type: 'success', text: 'Credential deleted' });
      fetchCredentials();
    } catch (error) { setMessage({ type: 'error', text: 'Error deleting credential' }); }
  };

  const handleTest = async (id) => {
    try {
      const res = await axios.post(`/admin/facebook/settings/credentials/${id}/test`);
      if (res.data.success) setMessage({ type: 'success', text: `Connection OK! Account: ${res.data.data.name}` });
      else setMessage({ type: 'error', text: `Connection failed: ${res.data.error}` });
    } catch (error) { setMessage({ type: 'error', text: 'Error testing connection' }); }
  };

  const handleToggleActive = async (cred) => {
    try {
      await axios.put(`/admin/facebook/settings/credentials/${cred.id}`, { is_active: !cred.is_active });
      fetchCredentials();
    } catch (error) { setMessage({ type: 'error', text: 'Error updating status' }); }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncOutput('');
    setSyncDialogOpen(true);
    try {
      const res = await axios.post('/admin/facebook/settings/sync');
      if (res.data.success) {
        setSyncOutput(res.data.output || 'Sync completed successfully');
        setMessage({ type: 'success', text: 'Sync completed!' });
        fetchCredentials();
      } else {
        setSyncOutput(res.data.error || 'Sync failed');
        setMessage({ type: 'error', text: 'Sync failed' });
      }
    } catch (error) {
      setSyncOutput('Error: ' + error.message);
      setMessage({ type: 'error', text: 'Error during sync' });
    } finally { setSyncing(false); }
  };

  const openEditDialog = (cred) => {
    setEditingCred(cred);
    setFormData({ name: cred.name || '', app_id: cred.app_id || '', app_secret: '', access_token: '', ad_account_id: cred.ad_account_id || '', business_portfolio: cred.business_portfolio || '' });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCred(null);
    setFormData({ name: '', app_id: '', app_secret: '', access_token: '', ad_account_id: '', business_portfolio: '' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('it-IT');
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Facebook API Settings</Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button variant="contained" color="primary" startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <Sync />} onClick={handleSync} disabled={syncing} size={isMobile ? 'small' : 'medium'}>
            {syncing ? 'Syncing...' : 'Sync'}
          </Button>
          <Button variant="outlined" startIcon={<Add />} onClick={() => { resetForm(); setDialogOpen(true); }} size={isMobile ? 'small' : 'medium'}>
            Add
          </Button>
        </Box>
      </Box>

      {message.text && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>}

      {/* Credentials Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>API Credentials</Typography>
          <TableContainer>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  {!isMobile && <TableCell>Ad Account</TableCell>}
                  <TableCell align="center">Active</TableCell>
                  {!isMobile && <TableCell>Last Sync</TableCell>}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {credentials.length === 0 ? (
                  <TableRow><TableCell colSpan={isMobile ? 3 : 5} align="center"><Typography color="text.secondary" py={3}>No credentials configured</Typography></TableCell></TableRow>
                ) : credentials.map((cred) => (
                  <TableRow key={cred.id} hover>
                    <TableCell>
                      <Typography fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{cred.name}</Typography>
                      {isMobile && <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{cred.ad_account_id}</Typography>}
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{cred.ad_account_id}</Typography>
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Switch checked={cred.is_active} onChange={() => handleToggleActive(cred)} color="success" size="small" />
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        {cred.last_sync ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{formatDate(cred.last_sync)}</Typography>
                                                        <Chip size="small" icon={<CheckCircle />} label="Synced" color="success" sx={{ mt: 0.5, fontSize: '0.65rem', height: 20, color: 'white', '& .MuiChip-icon': { color: 'white' } }} />
                          </Box>
                        ) : (
                          <Chip size="small" icon={<Error />} label="Never" color="warning" sx={{ fontSize: '0.65rem', height: 20 }} />
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <Tooltip title="Test"><IconButton size="small" color="info" onClick={() => handleTest(cred.id)}><PlayArrow fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditDialog(cred)}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(cred.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Sync Info */}
      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" fontWeight={600} mb={1} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Automatic Sync</Typography>
          <Typography variant="body2" color="text.secondary">Facebook data syncs daily at <strong>06:00</strong>.</Typography>
          {!isMobile && <Typography variant="body2" color="text.secondary" mt={1}>Syncs: spend, impressions, clicks, reach, frequency, CPC, CPL for all active credentials.</Typography>}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editingCred ? 'Edit Credential' : 'Add New Credential'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth required size={isMobile ? 'small' : 'medium'} placeholder="e.g., Promo del Web" />
            <TextField label="Business Portfolio" value={formData.business_portfolio} onChange={(e) => setFormData({ ...formData, business_portfolio: e.target.value })} fullWidth size={isMobile ? 'small' : 'medium'} placeholder="e.g., My Business Portfolio" />
            <TextField label="App ID" value={formData.app_id} onChange={(e) => setFormData({ ...formData, app_id: e.target.value })} fullWidth required size={isMobile ? 'small' : 'medium'} placeholder="e.g., 1234567890123456" />
            <TextField label="App Secret" value={formData.app_secret} onChange={(e) => setFormData({ ...formData, app_secret: e.target.value })} fullWidth type="password" size={isMobile ? 'small' : 'medium'} placeholder={editingCred ? 'Leave empty to keep current' : 'Enter app secret'} helperText={editingCred ? 'Leave empty to keep current value' : ''} />
            <TextField label="Access Token" value={formData.access_token} onChange={(e) => setFormData({ ...formData, access_token: e.target.value })} fullWidth type="password" multiline rows={2} size={isMobile ? 'small' : 'medium'} placeholder={editingCred ? 'Leave empty to keep current' : 'Enter access token'} helperText={editingCred ? 'Leave empty to keep current value' : ''} />
            <TextField label="Ad Account ID" value={formData.ad_account_id} onChange={(e) => setFormData({ ...formData, ad_account_id: e.target.value })} fullWidth required size={isMobile ? 'small' : 'medium'} placeholder="e.g., act_123456789" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!formData.name || !formData.app_id || !formData.ad_account_id || (!editingCred && (!formData.app_secret || !formData.access_token))}>
            {editingCred ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync Output Dialog */}
      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>Sync Output {syncing && <CircularProgress size={16} sx={{ ml: 1 }} />}</DialogTitle>
        <DialogContent>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#1e1e1e', maxHeight: { xs: 300, sm: 400 }, overflow: 'auto' }}>
            <pre style={{ margin: 0, color: '#00ff00', fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>{syncOutput || 'Waiting for output...'}</pre>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSyncDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FbSettings;
